import { formatDate, formatDateKey, formatDateTimeAt } from '@/shared/formatters/date';
import { ehErroHttp, temCodigoHttp } from '@/shared/utils/erroHttp';

import type {
  FormularioRegistroIntimacaoSentenca,
  IntimacaoSentencaRegistradaDaParte,
  MeioIntimacaoSentencaRegistro,
  ParteCompletaItem,
  RegistrarIntimacaoSentencaInput,
} from '@/features/secretaria-arbitral/types';

import { motivoAcaoLegivel } from './motivoLoteLabels';
import {
  dataAtoDoFormulario,
  impedimentoDaDataDoAto,
  JUSTIFICATIVA_REGISTRO_CITACAO_MIN,
  justificativaDoRegistroValida,
} from './registroCitacao';

export const MEIOS_REGISTRO_INTIMACAO_SENTENCA: Record<MeioIntimacaoSentencaRegistro, string> = {
  EMAIL: 'E-mail',
  WHATSAPP: 'WhatsApp',
  AR: 'AR (Carta com aviso de recebimento)',
};

export const FORMULARIO_REGISTRO_INTIMACAO_SENTENCA_VAZIO: FormularioRegistroIntimacaoSentenca = {
  meio: '',
  dia: '',
  hora: '',
  documentoId: '',
  justificativa: '',
};

/**
 * Só o campo do core decide: `podeRegistrar` é calculado pela mesma régua que recusa o registro
 * com 409 (etapa, requerido ativo intimado, ainda sem entrega). Sem o campo (core antigo), a ação
 * fica oculta.
 */
export function podeRegistrarIntimacaoSentenca(parte: ParteCompletaItem): boolean {
  return parte.intimacaoSentenca?.podeRegistrar === true;
}

/**
 * O que ainda impede confirmar, na ordem em que o formulário é preenchido, ou `null` — é a ÚNICA
 * condição do botão. `desde` é o dia da sentença (`AAAA-MM-DD`, São Paulo): o dia informado é
 * comparado como dia civil, com ou sem hora, porque é assim que a Secretaria o lê.
 */
export function impedimentoDoRegistroIntimacaoSentenca(
  formulario: FormularioRegistroIntimacaoSentenca,
  agora: Date,
  desde: string | null,
): string | null {
  if (!formulario.meio) return 'Escolha o meio da intimação.';
  const impedimentoDaData = impedimentoDaDataDoAto(formulario, agora, 'da intimação');
  if (impedimentoDaData) return impedimentoDaData;
  if (desde && formulario.dia < desde) {
    return `A data da intimação não pode ser anterior à sentença (${formatDateKey(desde)}).`;
  }
  if (formulario.meio === 'AR' && !formulario.documentoId) {
    return 'Intimação por AR exige o AR digitalizado como evidência.';
  }
  if (!justificativaDoRegistroValida(formulario.justificativa)) {
    return `Escreva a justificativa com no mínimo ${JUSTIFICATIVA_REGISTRO_CITACAO_MIN} caracteres.`;
  }
  return null;
}

export function montarRegistroIntimacaoSentencaInput(
  formulario: FormularioRegistroIntimacaoSentenca & { meio: MeioIntimacaoSentencaRegistro },
): RegistrarIntimacaoSentencaInput {
  return {
    meio: formulario.meio,
    dataAto: dataAtoDoFormulario(formulario),
    justificativa: formulario.justificativa.trim(),
    ...(formulario.documentoId ? { documentoId: formulario.documentoId } : {}),
  };
}

/**
 * Sem hora, `intimadoEm` é 00:00 de São Paulo do dia informado: mostrar a hora diria "00:00",
 * que ninguém informou.
 */
export function descricaoDoRegistroDaIntimacao(
  registro: IntimacaoSentencaRegistradaDaParte,
): string {
  const quando = registro.somenteDia
    ? formatDate(registro.intimadoEm)
    : formatDateTimeAt(registro.intimadoEm);
  return `Intimação da sentença registrada pela Secretaria: ${MEIOS_REGISTRO_INTIMACAO_SENTENCA[registro.meio]}, ${quando}`;
}

/**
 * As mensagens do core para estas recusas carregam ids (são diagnóstico); o operador precisa do
 * que fazer.
 */
export function mensagemDoRegistroIntimacaoSentenca(erro: unknown): string {
  if (temCodigoHttp(erro, 'INTIMACAO_SENTENCA_JA_ENTREGUE')) {
    return 'Este requerido já tem a intimação da sentença entregue — atualize a página para ver a situação atual.';
  }
  if (temCodigoHttp(erro, 'PARTE_NAO_E_REQUERIDO_ATIVO')) {
    return 'Só a intimação de requerido ativo pode ser registrada.';
  }
  if (temCodigoHttp(erro, 'PROCESSO_FORA_DA_INTIMACAO_DA_SENTENCA')) {
    return 'O processo não está mais na etapa de intimação da sentença — atualize a página.';
  }
  if (ehErroHttp(erro, 404)) {
    return 'O documento escolhido ou a parte não foi encontrado neste processo — atualize a página e tente de novo.';
  }
  return motivoAcaoLegivel(erro, 'Erro ao registrar a intimação da sentença');
}
